<script setup>
// NOTICE: KAPTION-INTEGRATION
// whatsapp-calling.WEB.4 — the voice_call message (CALL_MODEL.3) for every
// status: ringing, in progress, completed (duration + agent), missed,
// declined, failed, permission requested. The message is updated in place by
// Rails; while the call is live the call store's copy (fed by EVENTS.*) wins
// so the bubble moves with the call before message.updated lands.
//
// whatsapp-calling-ownership.UI.8 / PASS.7 — a call that was passed shows its
// legs as one compact chain ("Ana 2:10 → Bruno 4:32"), the full sentence
// ("Passed to Bruno by Ana") kept for screen readers, plus the last note.
//
// Warm Paper (feature: warm-paper-redesign): a card with an icon tile, rows
// separated by hairlines.
//
// whatsapp-call-transcripts.WEB.T6 — a recorded call carries a "Transcript"
// row: the disclosure folds out the first turns (compact reader, loaded on
// open), and the status chip ("Ready", "Transcribing"…) opens the call
// detail (D9: the Calls workspace). Status comes from the live call, else
// the message's content_attributes.data.transcript_status (EVENTS.2).
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import BaseBubble from 'next/message/bubbles/Base.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useMessageContext } from 'next/message/provider.js';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import { frontendURL } from 'dashboard/helper/URLHelper';
import KaptionCallTranscript from './KaptionCallTranscript.vue';
// NOTICE: KAPTION-INTEGRATION - whatsapp-calling-ai.UI.2, .5
import KaptionCallAiChip from './KaptionCallAiChip.vue';
import KaptionCallAiSummary from './KaptionCallAiSummary.vue';
import { useKaptionCallStore } from './kaptionCallStore';
import { useKaptionCallTranscripts } from './kaptionCallTranscriptStore';
import {
  TRANSCRIPT_STATUS,
  WORKING_STATUSES,
  normalizeTranscript,
} from './kaptionCallTranscriptModel';
import {
  CALL_STATUS,
  formatCallDuration,
  normalizeLegs,
  rawLegs,
  quoteNote,
  normalizeAiSummary,
} from './kaptionCallModel';
import { legChain } from './kaptionCallTimeline';
import {
  PAPER_ICON_TILE,
  TRANSCRIPT_ACCENT,
  TRANSCRIPT_ICON,
  TRANSCRIPT_TINT,
} from './kaptionCallPaper';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { contentAttributes, conversationId } = useMessageContext();
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
const calls = useKaptionCallStore();
const route = useRoute();
const router = useRouter();

const attrs = computed(() => contentAttributes.value || {});
const data = computed(() => attrs.value.data || attrs.value.kaptionCall || {});
const read = (obj, camel, snake) => obj?.[camel] ?? obj?.[snake];

const callId = computed(() =>
  read(attrs.value, 'kaptionCallId', 'kaption_call_id')
);
const liveCall = computed(() => calls.findCall(callId.value));

const status = computed(
  () => liveCall.value?.status || data.value.status || CALL_STATUS.RINGING
);
const direction = computed(
  () =>
    liveCall.value?.direction ||
    read(data.value, 'callDirection', 'call_direction') ||
    data.value.direction ||
    'inbound'
);
const isOutbound = computed(() => direction.value === 'outbound');
const durationSeconds = computed(
  () =>
    liveCall.value?.durationSeconds ??
    read(data.value, 'durationSeconds', 'duration_seconds')
);
const agentName = computed(
  () =>
    liveCall.value?.answeredBy?.name ||
    read(data.value, 'answeredBy', 'answered_by')?.name ||
    ''
);

// UI.8 — legs from the live call, else from the message data (snake or
// camelCase: MessageList deep-camelCases content_attributes).
const legs = computed(() => {
  const live = liveCall.value?.legs;
  if (live?.length) return live;
  return normalizeLegs(rawLegs(data.value) || []);
});
const chain = computed(() => legChain(legs.value));
// whatsapp-calling-ai.UI.2 — an AI-only call still shows "Asistente 1:40".
const showLegs = computed(
  () => legs.value.length > 1 || legs.value[0]?.kind === 'assistant'
);
// whatsapp-calling-ai.UI.5 — the AI summary, live or from the message data.
const aiSummary = computed(() =>
  isKaptionFlagEnabled('KAPTION_CALL_STUDIOCHAT_EVENTS')
    ? liveCall.value?.aiSummary || normalizeAiSummary(data.value.ai)
    : null
);
const lastNote = computed(
  () => [...chain.value].reverse().find(leg => leg.note)?.note || ''
);

const VISUALS = {
  [CALL_STATUS.RINGING]: {
    icon: 'i-lucide-phone-incoming',
    outboundIcon: 'i-lucide-phone-outgoing',
    tone: 'text-n-amber-11',
    title: 'RINGING_INBOUND',
    outboundTitle: 'RINGING_OUTBOUND',
    sub: 'RINGING_SUB',
  },
  [CALL_STATUS.ANSWERED]: {
    icon: 'i-lucide-phone-call',
    tone: 'text-n-teal-11',
    title: 'IN_PROGRESS',
    sub: 'IN_PROGRESS_SUB',
  },
  [CALL_STATUS.COMPLETED]: {
    icon: 'i-lucide-phone-incoming',
    outboundIcon: 'i-lucide-phone-outgoing',
    tone: 'text-n-slate-11',
    title: 'COMPLETED_INBOUND',
    outboundTitle: 'COMPLETED_OUTBOUND',
  },
  [CALL_STATUS.MISSED]: {
    icon: 'i-lucide-phone-missed',
    tone: 'text-n-ruby-11',
    title: 'MISSED',
    outboundTitle: 'NO_ANSWER',
    sub: 'MISSED_SUB',
    outboundSub: 'NO_ANSWER_SUB',
  },
  [CALL_STATUS.DECLINED]: {
    icon: 'i-lucide-phone-off',
    tone: 'text-n-ruby-11',
    title: 'DECLINED',
    sub: 'DECLINED_SUB',
  },
  [CALL_STATUS.FAILED]: {
    icon: 'i-lucide-circle-alert',
    tone: 'text-n-ruby-11',
    title: 'FAILED',
    sub: 'FAILED_SUB',
  },
  [CALL_STATUS.PERMISSION_REQUESTED]: {
    icon: 'i-lucide-shield-question',
    tone: 'text-n-blue-11',
    title: 'PERMISSION',
    sub: 'PERMISSION_SUB',
  },
};

const visual = computed(
  () => VISUALS[status.value] || VISUALS[CALL_STATUS.COMPLETED]
);
const icon = computed(() =>
  isOutbound.value && visual.value.outboundIcon
    ? visual.value.outboundIcon
    : visual.value.icon
);
const titleKey = computed(
  () =>
    `KAPTION_CALLS.BUBBLE.${(isOutbound.value && visual.value.outboundTitle) || visual.value.title}`
);
const subKey = computed(() => {
  const key =
    (isOutbound.value && visual.value.outboundSub) || visual.value.sub;
  return key ? `KAPTION_CALLS.BUBBLE.${key}` : null;
});
const completedLine = computed(() => {
  if (status.value !== CALL_STATUS.COMPLETED) return '';
  return formatCallDuration(durationSeconds.value);
});

const canCallBack = computed(
  () =>
    !isOutbound.value &&
    [CALL_STATUS.MISSED, CALL_STATUS.DECLINED, CALL_STATUS.FAILED].includes(
      status.value
    ) &&
    isKaptionFlagEnabled('KAPTION_WHATSAPP_CALLING') &&
    isKaptionFlagEnabled('KAPTION_WHATSAPP_CALLING_OUTBOUND') &&
    !calls.state.activeCallId
);

const callBack = () => calls.startOutbound(conversationId.value);

// whatsapp-call-transcripts.WEB.T6 — the Transcript row: an answered call
// that ended, with a transcript status, while transcription is on.
const transcripts = useKaptionCallTranscripts();
const transcriptStatus = computed(
  () =>
    transcripts.entry(callId.value)?.data?.status ||
    liveCall.value?.transcriptStatus ||
    read(data.value, 'transcriptStatus', 'transcript_status') ||
    null
);
const showTranscript = computed(
  () =>
    status.value === CALL_STATUS.COMPLETED &&
    !!callId.value &&
    !!transcriptStatus.value &&
    isKaptionFlagEnabled('KAPTION_WHATSAPP_CALLING') &&
    isKaptionFlagEnabled('KAPTION_CALL_TRANSCRIPTION')
);
const isWorking = computed(() =>
  WORKING_STATUSES.includes(transcriptStatus.value)
);
const chipTone = computed(() => {
  switch (transcriptStatus.value) {
    case TRANSCRIPT_STATUS.READY:
    case TRANSCRIPT_STATUS.PENDING:
    case TRANSCRIPT_STATUS.PROCESSING:
      return [TRANSCRIPT_TINT, TRANSCRIPT_ACCENT];
    case TRANSCRIPT_STATUS.PARTIAL:
      return ['bg-n-amber-3', 'text-n-amber-11'];
    case TRANSCRIPT_STATUS.FAILED:
      return ['bg-n-ruby-3', 'text-n-ruby-11'];
    default:
      return ['bg-n-alpha-2', 'text-n-slate-11'];
  }
});
const transcriptOpen = ref(false);
const transcriptCall = computed(
  () =>
    liveCall.value || {
      id: callId.value,
      contactName: '',
      legs: legs.value,
      answeredBy: agentName.value ? { name: agentName.value } : null,
      durationSeconds: durationSeconds.value,
    }
);
const transcriptEntry = computed(() => transcripts.entry(callId.value));
const transcript = computed(() =>
  transcriptEntry.value?.data
    ? normalizeTranscript(transcriptEntry.value.data, {
        customerName: transcriptCall.value.contactName,
      })
    : null
);
const toggleTranscript = () => {
  transcriptOpen.value = !transcriptOpen.value;
  if (transcriptOpen.value) transcripts.load(callId.value);
};
const openCallDetail = seconds => {
  const accountId = route.params.accountId;
  const query = { call: callId.value };
  if (typeof seconds === 'number') query.t = String(Math.round(seconds * 1000));
  router.push({
    path: frontendURL(`accounts/${accountId}/calls/all`),
    query,
  });
};
</script>

<template>
  <BaseBubble class="p-0 border-none" hide-meta data-bubble-name="kaption-call">
    <div
      data-testid="kaption-call-bubble"
      :data-status="status"
      class="flex w-80 max-w-full flex-col overflow-hidden rounded-xl border bg-n-solid-1 text-n-slate-12"
      :class="status === 'ringing' ? 'border-n-amber-8' : 'border-n-weak'"
    >
      <div class="flex items-center gap-3 px-3.5 py-3">
        <span class="size-9" :class="PAPER_ICON_TILE">
          <span class="size-[1.125rem]" :class="[icon, visual.tone]" />
        </span>
        <div class="flex min-w-0 flex-1 flex-col">
          <span
            data-testid="kaption-call-bubble-title"
            class="truncate text-sm font-medium"
          >
            {{ $t(titleKey) }}
          </span>
          <span
            v-if="completedLine"
            data-testid="kaption-call-bubble-sub"
            class="truncate text-xs text-n-slate-11"
          >
            {{
              agentName
                ? $t('KAPTION_CALLS.BUBBLE.COMPLETED_BY', {
                    duration: completedLine,
                    agent: agentName,
                  })
                : completedLine
            }}
          </span>
          <span
            v-else-if="status === 'answered' && agentName"
            data-testid="kaption-call-bubble-sub"
            class="truncate text-xs text-n-slate-11"
          >
            {{ $t('KAPTION_CALLS.BUBBLE.WITH_AGENT', { agent: agentName }) }}
          </span>
          <span
            v-else-if="subKey"
            data-testid="kaption-call-bubble-sub"
            class="truncate text-xs text-n-slate-11"
          >
            {{ $t(subKey) }}
          </span>
        </div>
        <span
          v-if="status === 'answered' || status === 'ringing'"
          class="size-2 shrink-0 rounded-full"
          :class="
            status === 'answered'
              ? 'animate-pulse bg-n-teal-9'
              : 'animate-pulse bg-n-amber-9'
          "
        />
      </div>

      <!-- UI.8 — "Ana 2:10 → Bruno 4:32" -->
      <div
        v-if="showLegs"
        class="flex flex-col gap-1 border-t border-n-weak px-3.5 py-2.5"
      >
        <ol
          data-testid="kaption-call-bubble-legs"
          class="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs"
        >
          <li
            v-for="(leg, index) in chain"
            :key="leg.key"
            data-testid="kaption-call-bubble-leg"
            class="flex items-center gap-1.5"
          >
            <span
              v-if="index > 0"
              class="i-lucide-arrow-right size-3 text-n-slate-9"
              aria-hidden="true"
            />
            <span class="sr-only">
              {{
                index === 0
                  ? $t('KAPTION_CALLS.BUBBLE.LEG_ANSWERED', { name: leg.name })
                  : $t('KAPTION_CALLS.BUBBLE.LEG_PASSED', {
                      name: leg.name,
                      from: leg.passedBy?.name || '',
                    })
              }}
              <template v-if="leg.note">{{ quoteNote(leg.note) }}</template>
            </span>
            <span aria-hidden="true" class="text-n-slate-12" :title="leg.name">
              {{ leg.shortName }}
            </span>
            <KaptionCallAiChip v-if="leg.assistant" icon-only />
            <span
              aria-hidden="true"
              class="font-mono tabular-nums text-n-slate-11"
            >
              {{ leg.duration }}
            </span>
          </li>
        </ol>
        <span
          v-if="lastNote"
          aria-hidden="true"
          class="truncate text-[11px] italic text-n-slate-11"
        >
          {{ quoteNote(lastNote) }}
        </span>
      </div>

      <!-- whatsapp-calling-ai.UI.5 — the AI call summary -->
      <div v-if="aiSummary" class="border-t border-n-weak px-3.5 py-2.5">
        <KaptionCallAiSummary :summary="aiSummary" compact />
      </div>

      <!-- whatsapp-call-transcripts.WEB.T6 — Transcript → call detail -->
      <div v-if="showTranscript" class="border-t border-n-weak">
        <div class="flex items-center gap-2 pr-3.5">
          <button
            type="button"
            data-testid="kaption-call-bubble-transcript"
            :aria-expanded="transcriptOpen"
            class="flex min-w-0 flex-1 items-center gap-2 py-2.5 pl-3.5 text-left transition-colors hover:bg-n-alpha-1"
            @click="toggleTranscript"
          >
            <span
              class="size-3.5 shrink-0"
              :class="[TRANSCRIPT_ICON, TRANSCRIPT_ACCENT]"
            />
            <span class="flex-1 truncate text-xs font-medium text-n-slate-12">
              {{ $t('KAPTION_CALLS.TRANSCRIPT.TITLE') }}
            </span>
            <span
              class="i-lucide-chevron-down size-3.5 shrink-0 text-n-slate-10 transition-transform"
              :class="{ 'rotate-180': transcriptOpen }"
            />
          </button>
          <button
            type="button"
            data-testid="kaption-call-bubble-transcript-chip"
            :data-status="transcriptStatus"
            class="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium leading-none transition-opacity hover:opacity-80"
            :class="chipTone"
            :title="$t('KAPTION_CALLS.TRANSCRIPT.OPEN_DETAIL')"
            @click="openCallDetail"
          >
            <Spinner v-if="isWorking" :size="10" />
            {{
              $t(
                `KAPTION_CALLS.TRANSCRIPT.STATUS.${String(transcriptStatus).toUpperCase()}`
              )
            }}
            <span class="i-lucide-arrow-up-right size-3" />
          </button>
        </div>
        <div
          v-if="transcriptOpen"
          data-testid="kaption-call-bubble-transcript-body"
          class="flex flex-col gap-1 px-2 pb-2"
        >
          <KaptionCallTranscript
            :call="transcriptCall"
            :transcript="transcript"
            :loading="!!transcriptEntry?.loading"
            :load-error="transcriptEntry?.error || null"
            compact
            @reload="transcripts.load(callId, { force: true })"
            @open-detail="openCallDetail"
          />
          <Button
            data-testid="kaption-call-bubble-open-detail"
            :label="$t('KAPTION_CALLS.TRANSCRIPT.OPEN_DETAIL')"
            icon="i-lucide-arrow-up-right"
            trailing-icon
            variant="ghost"
            color="slate"
            size="sm"
            class="self-center"
            @click="openCallDetail"
          />
        </div>
      </div>

      <div v-if="canCallBack" class="border-t border-n-weak px-3.5 py-2">
        <Button
          data-testid="kaption-call-bubble-call-back"
          icon="i-lucide-phone"
          :label="$t('KAPTION_CALLS.BUBBLE.CALL_BACK')"
          variant="link"
          size="sm"
          @click="callBack"
        />
      </div>
    </div>
  </BaseBubble>
</template>
